import { useState, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { scanLookup, issueItem, receiveItem } from '../lib/api';
import type { InventoryItem } from '../types';
import PageHeader from '../components/layout/PageHeader';
import BarcodeScanner from '../components/scanner/BarcodeScanner';

type Action = 'issue' | 'receive' | null;

export default function Scanner() {
  const [scannerActive, setScannerActive] = useState(false);
  const [scannedItem, setScannedItem] = useState<InventoryItem | null>(null);
  const [scanError, setScanError] = useState('');
  const [action, setAction] = useState<Action>(null);
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [done, setDone] = useState(false);

  const lookup = useMutation({
    mutationFn: (code: string) => scanLookup(code),
    onSuccess: (item) => { setScannedItem(item); setScanError(''); },
    onError: (e) => { setScanError((e as Error).message); setScannedItem(null); },
  });

  const act = useMutation({
    mutationFn: () => {
      if (!scannedItem || !action) throw new Error('Nothing to do');
      return action === 'issue'
        ? issueItem(scannedItem.id, qty, notes)
        : receiveItem(scannedItem.id, qty, notes);
    },
    onSuccess: () => { setDone(true); setScannerActive(false); },
  });

  const handleScan = useCallback((code: string) => {
    setScannerActive(false);
    setScannedItem(null);
    setScanError('');
    setAction(null);
    setDone(false);
    lookup.mutate(code);
  }, [lookup]);

  function reset() {
    setScannedItem(null);
    setScanError('');
    setAction(null);
    setDone(false);
    setQty(1);
    setNotes('');
    setScannerActive(true);
  }

  return (
    <div className="h-full flex flex-col">
      <PageHeader
        title="Scanner"
        breadcrumb={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Scanner' }]}
      />

      <div className="flex-1 overflow-y-auto p-6 max-w-lg">
        {/* Scanner */}
        {!done && (
          <>
            <div className="mb-4">
              <button
                className={scannerActive ? 'btn-secondary' : 'btn-primary'}
                onClick={() => setScannerActive((v) => !v)}
              >
                {scannerActive ? 'Stop Scanner' : 'Start Scanning'}
              </button>
            </div>
            <BarcodeScanner onScan={handleScan} active={scannerActive} />
          </>
        )}

        {/* Manual entry */}
        {!done && (
          <div className="mt-4">
            <label className="label text-xs">Or enter part number manually</label>
            <div className="flex gap-2">
              <input
                className="input flex-1"
                placeholder="e.g. MV8254"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleScan((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
            </div>
          </div>
        )}

        {/* Lookup loading */}
        {lookup.isPending && (
          <p className="text-sm text-gray-500 mt-4">Looking up part…</p>
        )}

        {/* Scan error */}
        {scanError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {scanError}
          </div>
        )}

        {/* Found item */}
        {scannedItem && !done && (
          <div className="mt-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="font-semibold text-gray-900">{scannedItem.name}</p>
              <p className="text-xs text-gray-500">{scannedItem.part_number}</p>
            </div>
            <div className="p-4">
              <p className="text-sm text-gray-600 mb-4">
                Current stock: <strong>{scannedItem.qty} {scannedItem.unit_of_measure}</strong>
              </p>

              {!action && (
                <div className="flex gap-2">
                  <button className="btn-primary flex-1" onClick={() => setAction('receive')}>
                    Receive
                  </button>
                  <button className="btn-danger flex-1" onClick={() => setAction('issue')}>
                    Issue
                  </button>
                </div>
              )}

              {action && (
                <div className="space-y-3">
                  <div>
                    <label className="label">Qty to {action}</label>
                    <input type="number" className="input" min={1} value={qty}
                      onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))} />
                  </div>
                  <div>
                    <label className="label">Notes</label>
                    <textarea className="input resize-none" rows={2}
                      value={notes} onChange={(e) => setNotes(e.target.value)} />
                  </div>
                  {act.isError && (
                    <p className="text-sm text-red-600">{(act.error as Error).message}</p>
                  )}
                  <div className="flex gap-2">
                    <button className="btn-secondary flex-1" onClick={() => setAction(null)}>Back</button>
                    <button
                      className={action === 'issue' ? 'btn-danger flex-1' : 'btn-primary flex-1'}
                      onClick={() => act.mutate()}
                      disabled={act.isPending}
                    >
                      {act.isPending ? 'Saving…' : `Confirm ${action}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Success */}
        {done && scannedItem && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
            <p className="text-green-700 font-medium">
              {action === 'issue' ? 'Issued' : 'Received'} {qty}× {scannedItem.name}
            </p>
            <button className="btn-primary mt-3" onClick={reset}>Scan Another</button>
          </div>
        )}
      </div>
    </div>
  );
}
